import _isEmpty from 'lodash/isEmpty';
import {
	exerciseConstants,
	ORIGINAL_WORD_INDEX,
	TRANSLATION_WORD_INDEX,
	DIACRITICS_CONV,
	DIACRITICS_REGEXP,
	FIND_BRACKETS_REGEXP,
	OPTIONS_FOR_REPEATING,
} from '../Constants';
import {
	MIN_ANSWERS_VARIANTS_FOR_MULTIPLE_TYPE,
	exerciseTypeCodes,
	PUNCTUATION_CHARACTERS,
} from '../../../services/Constants';
import i18n from 'i18next';

export const getLocaleList = (list_languages) => {
	const localeList = new Set();
	for (const prop in list_languages) {
		if (list_languages[prop].length) {
			for (const language of list_languages[prop]) {
				localeList.add(language.locale);
			}
		}
	}

	return localeList;
};

export const checkIsDirty = (configs, allConfiguration) => {
	let isDirty = false;

	for (const property in configs) {
		if (allConfiguration[property] !== configs[property]) {
			isDirty = true;
			break;
		}
	}

	return isDirty;
};

export const getLanguages = (list_id, list_languages) => {
	return list_languages[list_id];
};

export const getShuffledWord = (word) => {
	const wordForShuffle = word;
	return wordForShuffle
		.split('')
		.sort(() => Math.random() - 0.5)
		.join('');
};

export const isAllCharactersAreSame = (word) => {
	const uniqueCharacters = new Set();
	for (let i = 0; i < word.length; i++) {
		uniqueCharacters.add(word[i]);
	}
	return uniqueCharacters.size === 1;
};

export const getConsonantsLetters = (word) => {
	return (
		word &&
		word.split('').reduce((acc, letter) => {
			if (exerciseConstants.VOWELS.indexOf(letter) !== -1) acc = `${acc}.`;
			if (exerciseConstants.VOWELS.indexOf(letter) === -1) acc = `${acc}${letter}`;
			return acc;
		}, '')
	);
};

export const deactivatePressedElement = () => {
	document.activeElement.blur();
};

export const handleAnimation = (options) => {
	const { exerciseRef } = options;
	if (exerciseRef && exerciseRef.current)
		exerciseRef.current.setAttribute('class', 'exercise fade-right');
};

export const handleBottomBarAnimation = (options) => {
	const { exerciseTypes, isCorrect, successRespRef, errorRespRef } = options;
	const { isInYourMindType, isMultipleChoiceType } = exerciseTypes || {};
	const statusRefs = {
		success: successRespRef,
		error: errorRespRef,
	};
	const status = isCorrect ? 'success' : 'error';

	if (!isInYourMindType && statusRefs[status].current) {
		!isMultipleChoiceType
			? statusRefs[status].current.setAttribute(
					'class',
					`resp resp-${status} space-between fadeInUp`,
			  )
			: statusRefs[status].current.setAttribute('class', `resp resp-bottom-bar-${status}`);
	}
};

export const handleShowWord = (options) => {
	const { setShowWord, exerciseTypeCode, timeForHideWordInSpelling } = options;
	setShowWord(true);
	if (exerciseTypeCode === exerciseTypeCodes.SPELLING) {
		setTimeout(() => {
			setShowWord(false);
		}, timeForHideWordInSpelling);
	}
};

export const getAnimationHide = (timeForHideWordInSpelling) => {
	return setTimeout(() => true, Number(timeForHideWordInSpelling));
};

export const getExerciseTypesDependsOnWordsCount = (
	wordsCollectionLength,
	exercisesTypes,
	wordsWithPerformance,
) => {
	if (
		!wordsCollectionLength &&
		wordsWithPerformance.length >= MIN_ANSWERS_VARIANTS_FOR_MULTIPLE_TYPE
	) {
		return exercisesTypes;
	}

	if (wordsCollectionLength >= MIN_ANSWERS_VARIANTS_FOR_MULTIPLE_TYPE) return exercisesTypes;

	return exercisesTypes.filter((item) => item.code !== exerciseTypeCodes.MULTIPLE_CHOICE);
};

export const getOptionsForRepeating = (allowedNumRounds) => {
	return OPTIONS_FOR_REPEATING.map((item, index) => {
		const availableRounds = allowedNumRounds.length - 1;
		const isDisabled = index > availableRounds;

		return {
			...item,
			disabled: isDisabled,
			label: isDisabled ? i18n.t('general.upgrade') : '',
		};
	});
};

export const getLangIndex = (allConfiguration) =>
	allConfiguration.answer_subject_index ? ORIGINAL_WORD_INDEX : TRANSLATION_WORD_INDEX;

export const handleApostropheCode = (word = '') => {
	const apostropheRegex = /[\u0027\u2018\u2019\u201B\u2032\u02BC\u02BB\u02BD\u02B9\u02BE\u02BF]/g;

	return String(word).trim()?.replace(apostropheRegex, "'");
};

const checkCapitals_ = (word) => {
	return word.toLowerCase();
};

const checkPunctuation_ = (word) => {
	return word.filter((item) => PUNCTUATION_CHARACTERS.indexOf(item) === -1).join('');
};

const checkDiacritics_ = (word) => {
	let diacriticsWord = word.normalize('NFD').replace(/[\u0300-\u036f]/g, ''); // get rid of accents

	if (!diacriticsWord.match(DIACRITICS_REGEXP)) return diacriticsWord; // decompose ligatures into separate letters

	for (let i = 0; i < DIACRITICS_CONV.length; i++) {
		diacriticsWord = diacriticsWord.replace(
			new RegExp(unescape(DIACRITICS_CONV[i][0]), 'g'),
			unescape(DIACRITICS_CONV[i][1]),
		);
	}
	return diacriticsWord;
};

export const checkOnRules = (
	allConfiguration,
	proposedWord,
	answerWord,
	splittedProposedWord,
	splittedAnswerWord,
) => {
	const { capitals, punctuation, diacritics } = allConfiguration;
	let userAnswer = proposedWord;
	let expectedAnswer = answerWord;

	if (!punctuation && splittedProposedWord) {
		userAnswer = checkPunctuation_(splittedProposedWord);
		expectedAnswer = checkPunctuation_(splittedAnswerWord);
	}

	if (!capitals) {
		userAnswer = checkCapitals_(userAnswer);
		expectedAnswer = checkCapitals_(expectedAnswer);
	}

	if (!diacritics) {
		userAnswer = checkDiacritics_(userAnswer);
		expectedAnswer = checkDiacritics_(expectedAnswer);
	}
	return { userAnswer, expectedAnswer };
};

export const validationAnswerWord = (proposedWord, answerWord, allConfiguration) => {
	if (proposedWord && answerWord && String(proposedWord).trim() === String(answerWord).trim()) {
		return { isCorrect: true, isCausedByCheckOnRules: false, typoError: false };
	}
	const hasBracketsProposedWord = proposedWord && proposedWord.match(FIND_BRACKETS_REGEXP);
	const hasBracketsAnswerWord = answerWord && answerWord.match(FIND_BRACKETS_REGEXP);
	let answerWord_ = answerWord ? handleApostropheCode(answerWord) : '';
	let proposedWord_ = proposedWord ? handleApostropheCode(proposedWord) : '';
	const splittedProposedWord = proposedWord_?.split('');

	if (!hasBracketsProposedWord && hasBracketsAnswerWord) {
		answerWord_ = answerWord_.replace(FIND_BRACKETS_REGEXP, '').trim();
	}

	const splittedAnswerWord = answerWord_ ? answerWord_.split('/') : [];
	let result;
	let isFailedOnCheckOnRules = false;
	let typoError = true;

	const { userAnswer, expectedAnswer } = checkOnRules(
		allConfiguration,
		proposedWord_,
		answerWord_,
		splittedProposedWord,
		answerWord_.split(''),
	);
	proposedWord_ = userAnswer;
	answerWord_ = expectedAnswer;

	for (const word_ of splittedAnswerWord) {
		const splittedAnswerWord_ = word_.split('');
		const isCorrect = proposedWord_ === word_.trim() || proposedWord_ === answerWord_.trim();
		answerWord_ = word_.trim();

		if (isCorrect) return { isCorrect: true, isCausedByCheckOnRules: false };
		const checkProposedWord_ = splittedProposedWord || proposedWord_;
		const checkAnswerWord_ = splittedAnswerWord_ || answerWord_;

		isFailedOnCheckOnRules =
			checkProposedWord_ &&
			isFailedBecauseOfCheckOnRules(checkProposedWord_, checkAnswerWord_);

		const { userAnswer: userAnswerSplitted, expectedAnswer: expectedAnswerSplitted } =
			checkOnRules(
				allConfiguration,
				proposedWord_,
				answerWord_,
				splittedProposedWord,
				splittedAnswerWord_,
			);
		proposedWord_ = userAnswerSplitted;
		answerWord_ = expectedAnswerSplitted;

		if (proposedWord_ === answerWord_) {
			result = true;
			break;
		}
		result = proposedWord_.trim() === answerWord_.trim();

		typoError = proposedWord_.trim().length === answerWord_.trim().length;
	}

	return {
		isCorrect: result,
		isCausedByCheckOnRules: !result && isFailedOnCheckOnRules,
		typoError,
	};
};

const isFailedBecauseOfCheckOnRules = (proposedWord, answerWord) => {
	const { userAnswer, expectedAnswer } = checkOnRules(
		{ capitals: false, punctuation: false, diacritics: false },
		proposedWord,
		answerWord,
		proposedWord,
		answerWord,
	);

	return userAnswer.trim() === expectedAnswer.trim();
};

export const setFocusToInput = (exerciseRef) => {
	const element = exerciseRef.current.getElementsByTagName('input')[0];
	element && element.focus();
};

export const handleWordRounds = (wordQueue, rounds, prevRounds) => {
	let newWordQueue = [];

	if (rounds - prevRounds > 0) {
		for (let i = 1; i <= rounds; i++) {
			newWordQueue = newWordQueue.concat(
				wordQueue.normal.map((item) => ({ ...item, round_nr: i })),
			);
		}
	} else {
		newWordQueue = wordQueue.normal.filter((item) => item.round_nr <= rounds);
	}

	return { normal: newWordQueue, incorrect: wordQueue.incorrect };
};

export const getLocaleName = (languagesArr, locale) =>
	languagesArr.filter((item) => item.locale === locale)[0].name;

export const getFlagCode = (languagesArr, locale) =>
	languagesArr.filter((item) => item.locale === locale)[0].flag_code;

export const getOptionsForTime = (options) =>
	options.map((i) => ({
		value: i.toString(),
		title: (i / 1000).toString(),
	}));

export const isItemExistInArray = (arr, item, key) =>
	!!arr.length && arr.filter((el) => el[key] === item[key]).length;

const filterValidItems = (array) =>
	Array.isArray(array)
		? array.filter((item) => Object.keys(item).length > 0 && item.word_id)
		: [];

export const getInitialWordQueue = (data) => {
	if (_isEmpty(data.word_queue) && data.exercise_type_code !== 'learn') {
		const answerLocale = data.all_configuration.answer_locale;
		const questionLocale = data.all_configuration.question_locale;

		return {
			...data,
			word_queue: {
				normal: data.words.map((item) => ({
					display_type: data.exercise_type_code,
					word_id: item.id,
					correct: null,
					answer_locale: answerLocale,
					question_locale: questionLocale,
					list_id: item.list_id,
					round_nr: 1,
				})),
				incorrect: [],
			},
		};
	}

	if (_isEmpty(data.word_queue) && data.exercise_type_code === 'learn') {
		return {
			...data,
			word_queue: {
				normal: [],
				incorrect: [],
				need_to_practise_words: data.words,
				in_progress: [],
				block_words: [],
				current_block_number: 1,
				learned_words_count: 0,
			},
		};
	}
	return {
		...data,
		word_queue: {
			...data.word_queue,
			normal: filterValidItems(data.word_queue?.normal),
			incorrect: filterValidItems(data.word_queue?.incorrect),
		},
	};
};

export const underlineDifference = (word1, word2) => {
	let text = '';
	if (word1 && word2) {
		[...word1.trim()].forEach((val, i) => {
			if (val !== word2.trim().charAt(i)) text += `<span class='underline'>${val}</span>`;
			else text += val;
		});
		return text;
	}
	return text;
};

export const getTypoPairs = (wordTuples) =>
	wordTuples.reduce((acc, item) => {
		item.correct_strings.forEach((el) => {
			acc.push({ input_string: item.input_string, correct_string: el, typo: false });
		});
		return acc;
	}, []);

export const removeIncorrectData = (words) =>
	words.length ? words.filter((item) => item.round_nr) : words;

export const processString = (word, isCapitalConfigEnabled) =>
	isCapitalConfigEnabled ? word : word.toLowerCase();
